import { CheckCircle2, XCircle, HelpCircle, AlertTriangle, Info } from 'lucide-react';

// LINE READINESS — shared rendering for `GET /readiness`, `GET /tenants/:id/readiness`
// and `GET /admin/line-readiness` (core: src/routes/readiness.js).
//
// Contract this file is written against — docs/line-readiness-endpoint-2026-07-27.md:
//
//   1. `ok: null` means UNKNOWN, never broken. If LiveKit or Telnyx was
//      unreachable the check is null and the whole result is status:'unknown'.
//      A line must NEVER be painted dead because an API timed out.
//   2. `live` is BLOCKERS ONLY. Warnings and info never flip it.
//   3. `live` (the line answers) is not `scheduleReady` (a timezone is chosen).
//      Timezone blocks campaign SCHEDULING, not answering — conflating them
//      would paint a healthy inbound line as dead.
//
// And the product rule from the provisioning assignment: the UI must never imply
// a line is set up until it is green. Anything short of green shows the named
// blocker VERBATIM — the whole point is to end the multi-system debug hunt, so
// we surface core's `fix` text rather than inventing our own wording.

const TONES = {
  live: {
    label: 'Live',
    Icon: CheckCircle2,
    badge: 'bg-emerald-950/60 text-emerald-300',
    text: 'text-emerald-300'
  },
  blocked: {
    label: 'Not live',
    Icon: XCircle,
    badge: 'bg-rose-950/60 text-rose-300',
    text: 'text-rose-300'
  },
  unknown: {
    label: 'Unverified',
    Icon: HelpCircle,
    badge: 'bg-amber-950/60 text-amber-300',
    text: 'text-amber-300'
  }
};

export function readinessTone(status) {
  return TONES[status] || TONES.unknown;
}

// The one-glance verdict. `unknown` deliberately reads as "we could not check",
// not as a failure — see rule 1.
export function ReadinessBadge({ status, className = '' }) {
  const tone = readinessTone(status);
  const { Icon } = tone;
  return (
    <span className={`badge gap-1 ${tone.badge} ${className}`}>
      <Icon size={12} />
      {tone.label}
    </span>
  );
}

// Mirrors core's normalizeDid (lineReadinessService.js): core always answers in
// normalized form, but a phone_numbers row was not necessarily STORED that way —
// seeds, CSVs and humans do not. Compare on digits, or a row saved as
// "18652730436" silently gets no verdict and then reappears in the wrong list.
export function normalizeDid(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
}

export function sameDid(a, b) {
  const na = normalizeDid(a);
  const nb = normalizeDid(b);
  return !!na && na === nb;
}

// Checks scoped to one DID: core stamps `did` on per-number checks and leaves it
// null on tenant-wide ones, so a number's full story is its own checks plus the
// tenant's. Pass did=null to get everything.
export function checksForDid(checks, did) {
  const all = Array.isArray(checks) ? checks : [];
  if (!did) return all;
  return all.filter((c) => !c.did || sameDid(c.did, did));
}


function severityIcon(check) {
  if (check.ok === false && check.severity === 'blocker') return XCircle;
  if (check.ok === false || check.severity === 'warn') return AlertTriangle;
  if (check.ok === null) return HelpCircle;
  return Info;
}

function severityClass(check) {
  if (check.ok === false && check.severity === 'blocker') return 'text-rose-300';
  if (check.ok === false || check.severity === 'warn') return 'text-amber-300';
  if (check.ok === null) return 'text-amber-300';
  return 'text-stone-400';
}

// The explanation. Blockers first, then warnings, then unknowns — the order
// someone fixing a dead line needs them in.
//
// `notes` is the category that matters most on a HEALTHY line: checks that
// passed but carry context the badge can't (`demo_override` saying a demo owned
// by a DIFFERENT tenant takes this number; `alternate_agent_stack` saying a
// second deployment answers it, so this tenant's persona decides nothing).
// Those are `ok: true, severity: 'info'` — dropping them because they passed is
// exactly the false all-clear core added the check to prevent.
export function ReadinessChecks({ checks, did = null, showPassing = false }) {
  const scoped = checksForDid(checks, did);

  const blockers = scoped.filter((c) => c.ok === false && c.severity === 'blocker');
  const warnings = scoped.filter((c) => c.ok === false && c.severity !== 'blocker');
  // `env_shadow` is info + permanently null (core can't read the box .env). It
  // would otherwise print "couldn't determine" on every line forever.
  const unknowns = scoped.filter((c) => c.ok === null && c.severity !== 'info');
  const notes = scoped.filter((c) => c.ok === true && c.severity !== 'blocker');
  const passing = scoped.filter((c) => c.ok === true && c.severity === 'blocker');

  const shown = [
    ...blockers,
    ...warnings,
    ...unknowns,
    ...notes,
    ...(showPassing ? passing : [])
  ];
  if (shown.length === 0) {
    return <p className="text-xs text-stone-500">Every check passed.</p>;
  }

  return (
    <ul className="space-y-2">
      {shown.map((c) => {
        const Icon = severityIcon(c);
        return (
          <li key={`${c.id}:${c.did || 'tenant'}`} className="flex gap-2 text-xs">
            <Icon size={14} className={`mt-0.5 shrink-0 ${severityClass(c)}`} />
            <div className="min-w-0">
              <div className={severityClass(c)}>{c.label}</div>
              {c.detail && <div className="text-stone-500">{c.detail}</div>}
              {/* core writes `fix` for a human — show it verbatim, never paraphrased. */}
              {c.fix && <div className="mt-0.5 text-stone-300">→ {c.fix}</div>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// Shown when core answers 404 — the readiness routes are not deployed on this
// server yet. Deliberately NOT an error: nothing is wrong with the line, we just
// have no verdict, and an error banner here would read as a broken line.
export function ReadinessUnavailable() {
  return (
    <p className="text-xs text-stone-500">
      Line readiness isn’t available on this server yet.
    </p>
  );
}

// Shown when the readiness call itself failed (500, network, timeout) — as
// opposed to 404, which is ReadinessUnavailable. Without this, a failed check
// renders as total silence and is indistinguishable from "not deployed yet" or
// from the feature simply not being there.
export function ReadinessError({ message }) {
  return (
    <p className="text-xs text-amber-300">
      Couldn’t check whether these lines are live{message ? ` — ${message}` : ''}.
    </p>
  );
}

// Campaign scheduling is a separate gate from answering (rule 3). Only ever
// rendered as an advisory next to a line that is otherwise fine.
export function ScheduleReadyNote({ scheduleReady }) {
  if (scheduleReady !== false) return null;
  return (
    <p className="mt-2 text-xs text-amber-300">
      Campaign scheduling is blocked until a timezone is chosen — the line still answers.
    </p>
  );
}
